import {
  ArrowRight,
  Check,
  Star,
  Zap,
  Brain,
  Target,
  Shield,
  BarChart,
  Users,
  Settings,
  Sparkles,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MagicCard } from "@/components/ui/magic-card";
import { motion } from "framer-motion";
import { FEATURES } from "@/constants/features";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { OrbitingCircles } from "@/components/ui/orbiting-circles";
import Icons from "@/components/global/icons";

const FeaturesPage = () => {
  // Feature flags for dynamic content
  const isGlobalCdnEnabled = useFeatureFlag("globalCdn");
  const isPerformanceEnabled = useFeatureFlag("performance");
  const isSecurityEnabled = useFeatureFlag("security");
  const isAiSolutionsEnabled = useFeatureFlag("aiSolutions");
  const isDevelopmentEnabled = useFeatureFlag("development");
  const isAnalyticsEnabled = useFeatureFlag("analytics");
  const isAccessControlEnabled = useFeatureFlag("accessControl");
  const isRealtimeEnabled = useFeatureFlag("realtime");

  // Filter features based on feature flags
  const enabledFeatures = FEATURES.filter((feature) => {
    switch (feature.title) {
      case "Global CDN":
        return isGlobalCdnEnabled;
      case "Performance":
        return isPerformanceEnabled;
      case "Security":
        return isSecurityEnabled;
      case "AI Solutions":
        return isAiSolutionsEnabled;
      case "Development":
        return isDevelopmentEnabled;
      case "Analytics":
        return isAnalyticsEnabled;
      case "Access Control":
        return isAccessControlEnabled;
      case "Real-time":
        return isRealtimeEnabled;
      default:
        return true;
    }
  });

  const benefits = [
    {
      icon: Zap,
      title: "10x Faster Content Creation",
      description:
        "Generate high-quality marketing content in seconds, not hours.",
    },
    {
      icon: Target,
      title: "300% Better Targeting",
      description: "AI-powered audience insights for laser-focused campaigns.",
    },
    {
      icon: BarChart,
      title: "Real-time Analytics",
      description: "Make data-driven decisions with live performance metrics.",
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Seamless workflow for marketing teams of any size.",
    },
  ];

  const testimonial = {
    quote:
      "Blyrha has revolutionized our marketing workflow. The AI-powered insights have helped us achieve a 250% increase in campaign performance.",
    author: "Jessica Martinez",
    role: "Marketing Director",
    company: "TechStartup Inc.",
    rating: 5,
  };

  return (
    <Wrapper className="py-20 relative" data-oid="eq7egtm">
      {/* Hero Section with Orbiting Elements */}
      <div
        className="relative flex flex-col items-center justify-center w-full py-20"
        data-oid="q9lz2v1"
      >
        {/* Background blur effect */}
        <div
          className="absolute flex lg:hidden size-40 rounded-full bg-blue-500 blur-[10rem] top-0 left-1/2 -translate-x-1/2 -z-10"
          data-oid="9h5.f6c"
        ></div>

        {/* Orbiting circles for desktop */}
        <Container
          className="hidden lg:flex absolute inset-0 top-0 mb-auto flex-col items-center justify-center w-full min-h-[60vh] -z-10"
          data-oid="fv2tw52"
        >
          <OrbitingCircles speed={0.3} radius={250} data-oid="0men0jg">
            <Icons.circle1
              className="size-3 text-blue-500/60"
              data-oid=":_v6bf:"
            />
            <Icons.circle2
              className="size-1 text-purple-500/70"
              data-oid="9umijkb"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.15} radius={350} data-oid="ba:elww">
            <Icons.circle2
              className="size-1 text-pink-500/50"
              data-oid="1tv2dnu"
            />
            <Icons.circle1
              className="size-4 text-blue-500/50"
              data-oid="l:7c-ue"
            />
            <Icons.circle2
              className="size-1 text-purple-500/80"
              data-oid="ol6e9yf"
            />
          </OrbitingCircles>
        </Container>

        <Container data-oid=".0wtqg2">
          <div
            className="text-center max-w-4xl mx-auto mb-20 relative z-10"
            data-oid="_03e:3:"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-oid="l0bnsfr"
            >
              <div
                className="group relative grid overflow-hidden rounded-full px-4 py-2 shadow-[0_1000px_0_0_hsl(0_0%_15%)_inset] transition-colors duration-200 mx-auto mb-6 w-fit"
                data-oid="pbw_jew"
              >
                <span data-oid="junulx1">
                  <span
                    className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,white_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]"
                    data-oid="-64:u2o"
                  />
                </span>
                <span
                  className="backdrop absolute inset-[1px] rounded-full bg-background transition-colors duration-200 group-hover:bg-neutral-800"
                  data-oid="dvit8xf"
                />
                <span
                  className="z-10 py-1 text-sm text-neutral-100 flex items-center"
                  data-oid="b4boyv."
                >
                  <Sparkles
                    className="h-4 w-4 mr-2 text-blue-500"
                    data-oid="fpzmnth"
                  />
                  Powered by Advanced AI
                </span>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 !leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-oid="-uf9rv4"
            >
              Every Feature You Need for{" "}
              <span
                className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                data-oid="w7qz37h"
              >
                AI Marketing Success
              </span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-oid="x5rc0c."
            >
              From intelligent content generation to advanced analytics, Blyrha
              provides everything you need to transform your marketing with the
              power of AI.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-oid="b5op6yh"
            >
              <Link to="/demo" className="group" data-oid="_r9xjjc">
                <Button
                  size="lg"
                  className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25"
                  data-oid="5n7vg17"
                >
                  Start Free Trial
                  <ArrowRight
                    className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                    data-oid="r_rxuxx"
                  />
                </Button>
              </Link>
              <Link to="/pricing" data-oid="h1b977w">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 border-border/50 hover:bg-background/80 backdrop-blur-sm"
                  data-oid="hb-a6:u"
                >
                  View Pricing
                </Button>
              </Link>
            </motion.div>
          </div>
        </Container>
      </div>

      {/* Benefits Section with MagicCards */}
      <Container data-oid="ds9ta2j">
        <div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
          data-oid="jw1k-av"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Container
                key={index}
                delay={0.1 + index * 0.1}
                data-oid="qjx6mgr"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  className="text-center p-6 rounded-2xl group hover:scale-105 transition-transform duration-300"
                  gradientColor="rgba(59,130,246,0.1)"
                  data-oid=".ib4k8o"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    data-oid="s32i_ga"
                  >
                    <Icon
                      className="h-12 w-12 text-blue-600 mx-auto mb-4"
                      data-oid="sx7cdav"
                    />
                  </motion.div>
                  <h3 className="text-lg font-semibold mb-2" data-oid=":w8lj3i">
                    {benefit.title}
                  </h3>
                  <p
                    className="text-muted-foreground text-sm"
                    data-oid="-u15qk6"
                  >
                    {benefit.description}
                  </p>
                </MagicCard>
              </Container>
            );
          })}
        </div>
      </Container>

      {/* Main Features Grid with Enhanced Design */}
      <Container data-oid="m357o1g">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="58umwam"
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            data-oid="6_jisan"
          >
            Comprehensive{" "}
            <span
              className="font-subheading italic text-blue-600"
              data-oid="00q60m5"
            >
              Feature Set
            </span>
          </h2>
          <p
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
            data-oid="ku6gy7t"
          >
            Explore all the powerful tools and capabilities that make Blyrha the
            complete AI marketing solution.
          </p>
        </motion.div>

        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
          data-oid="u8cpbif"
        >
          {enabledFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Container
                key={feature.title}
                delay={0.1 + index * 0.1}
                data-oid="wl55l4r"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  className="group p-6 rounded-2xl hover:scale-105 transition-transform duration-300"
                  gradientColor="rgba(59,130,246,0.1)"
                  data-oid="8.hj5:j"
                >
                  <div className="relative" data-oid="7ezvb3d">
                    <motion.div
                      className={`w-12 h-12 rounded-lg ${feature.color} p-2 mb-4`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                      data-oid="a131:0p"
                    >
                      <Icon
                        className="w-full h-full text-white"
                        data-oid="ifx_3ve"
                      />
                    </motion.div>

                    <h3
                      className="text-xl font-semibold mb-3 flex items-center gap-2"
                      data-oid="gpmqm:m"
                    >
                      {feature.title}
                      <Activity
                        className="h-4 w-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-oid="yppopj0"
                      />
                    </h3>
                    <p
                      className="text-muted-foreground mb-4"
                      data-oid="n.x4x3b"
                    >
                      {feature.description}
                    </p>

                    {feature.details?.features && (
                      <ul className="space-y-2 mb-6" data-oid=":cpj-kd">
                        {feature.details.features
                          .slice(0, 3)
                          .map((highlight: string, highlightIndex: number) => (
                            <motion.li
                              key={highlightIndex}
                              className="flex items-center gap-2 text-sm"
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: highlightIndex * 0.1 }}
                              viewport={{ once: true }}
                              data-oid="r81_dwn"
                            >
                              <Check
                                className="w-4 h-4 text-green-500 flex-shrink-0"
                                data-oid="ai9x-vi"
                              />
                              {highlight}
                            </motion.li>
                          ))}
                      </ul>
                    )}

                    <Link to={`/features/${feature.slug}`} data-oid="d9li:y1">
                      <Button
                        variant="outline"
                        className="w-full group-hover:bg-blue-50 group-hover:border-blue-200 transition-all duration-300"
                        data-oid="h1e59q1"
                      >
                        Explore {feature.title}
                        <ArrowRight
                          className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                          data-oid="uubtfa3"
                        />
                      </Button>
                    </Link>
                  </div>
                </MagicCard>
              </Container>
            );
          })}
        </div>
      </Container>

      {/* Enhanced Testimonial Section */}
      <Container data-oid="_k41y3q">
        <motion.div
          className="text-center max-w-4xl mx-auto mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="z:s9opz"
        >
          <MagicCard
            gradientFrom="#3b82f6"
            gradientTo="#ec4899"
            className="p-8 rounded-2xl bg-gradient-to-r from-blue-50/50 to-purple-50/50 backdrop-blur-sm"
            gradientColor="rgba(59,130,246,0.05)"
            data-oid="92dgbrd"
          >
            <motion.div
              className="flex justify-center mb-4"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-oid="v3w72c_"
            >
              {[...Array(testimonial.rating)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, rotate: -180 }}
                  whileInView={{ opacity: 1, rotate: 0 }}
                  transition={{ delay: i * 0.1 + 0.3, duration: 0.3 }}
                  viewport={{ once: true }}
                  data-oid="pma::zq"
                >
                  <Star
                    className="w-5 h-5 text-yellow-400 fill-current"
                    data-oid="ppz.fbo"
                  />
                </motion.div>
              ))}
            </motion.div>
            <blockquote
              className="text-xl md:text-2xl font-medium mb-6 italic"
              data-oid="9o5zt:r"
            >
              "{testimonial.quote}"
            </blockquote>
            <div data-oid="fl.90_p">
              <p className="font-semibold text-lg" data-oid="353w1xi">
                {testimonial.author}
              </p>
              <p className="text-muted-foreground" data-oid="ruzv4c8">
                {testimonial.role} at {testimonial.company}
              </p>
            </div>
          </MagicCard>
        </motion.div>
      </Container>

      {/* Enhanced Integration Preview */}
      <Container data-oid="1:4p4my">
        <motion.div
          className="grid md:grid-cols-2 gap-12 items-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="il0e1p5"
        >
          <div data-oid="sssiw1f">
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              data-oid="58t1gjh"
            >
              Seamless{" "}
              <span className="text-blue-600" data-oid="-ap0nro">
                Integrations
              </span>
            </h2>
            <p
              className="text-muted-foreground mb-6 text-lg"
              data-oid="3ivv35y"
            >
              Connect Blyrha with your existing marketing stack. We integrate
              with over 100+ popular tools and platforms to streamline your
              workflow.
            </p>
            <ul className="space-y-4 mb-8" data-oid="nc_i.ba">
              {[
                "CRM platforms (Salesforce, HubSpot, Pipedrive)",
                "Social media platforms (Facebook, Instagram, LinkedIn)",
                "Email marketing tools (Mailchimp, SendGrid, ConvertKit)",
                "Analytics platforms (Google Analytics, Mixpanel)",
              ].map((integration, index) => (
                <motion.li
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/50"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-oid="ac5v51c"
                >
                  <Check
                    className="w-5 h-5 text-green-500 flex-shrink-0"
                    data-oid="xrb:ehy"
                  />
                  <span data-oid="2h-.v2y">{integration}</span>
                </motion.li>
              ))}
            </ul>
            <Link to="/" className="group" data-oid="6f9xy0a">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
                data-oid="bn.nrkr"
              >
                View All Integrations
                <ArrowRight
                  className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                  data-oid="vnjxdng"
                />
              </Button>
            </Link>
          </div>
          <div className="relative" data-oid="1o7w_ff">
            <MagicCard
              gradientFrom="#3b82f6"
              gradientTo="#8b5cf6"
              className="rounded-2xl overflow-hidden"
              gradientColor="rgba(59,130,246,0.1)"
              data-oid="y33y9cx"
            >
              <div
                className="relative rounded-2xl border border-border/50 bg-background/80 backdrop-blur-sm overflow-hidden"
                data-oid="k:zrwsi"
              >
                <div
                  className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10"
                  data-oid="u.a-ia4"
                />
                <img
                  src="/images/integrations-preview.jpg"
                  alt="Platform Integrations"
                  className="rounded-2xl w-full h-auto relative z-10"
                  onError={(e) => {
                    // Enhanced fallback with animated gradient
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.background =
                      "linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)";
                    target.style.height = "300px";
                    target.style.display = "block";
                    target.style.backgroundSize = "200% 200%";
                    target.style.animation = "gradient 3s ease infinite";
                  }}
                  data-oid="2bz02t-"
                />

                <div
                  className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-2xl"
                  data-oid="357cvtm"
                ></div>
              </div>
            </MagicCard>
          </div>
        </motion.div>
      </Container>

      {/* Enhanced How It Works */}
      <Container data-oid="h:nh:n0">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="h8ycoez"
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            data-oid="o2spbzf"
          >
            How It{" "}
            <span
              className="font-subheading italic text-purple-600"
              data-oid="43rdo3h"
            >
              Works
            </span>
          </h2>
          <p
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
            data-oid="nuj_-qh"
          >
            Getting started with AI-powered marketing is easier than you think.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-20" data-oid="xoym2fr">
          {[
            {
              step: "01",
              title: "Connect Your Data",
              description:
                "Import your existing marketing data and connect your favorite tools. Our AI learns from your brand and audience.",
              icon: Settings,
            },
            {
              step: "02",
              title: "AI Analysis & Insights",
              description:
                "Our advanced AI analyzes your data to identify patterns, opportunities, and optimization strategies.",
              icon: Brain,
            },
            {
              step: "03",
              title: "Automated Optimization",
              description:
                "Watch as AI automatically optimizes your campaigns, generates content, and improves performance in real-time.",
              icon: Zap,
            },
          ].map((step, index) => {
            const Icon = step.icon;
            return (
              <Container
                key={index}
                delay={0.1 + index * 0.2}
                data-oid="-sgiwbo"
              >
                <MagicCard
                  gradientFrom="#3b82f6"
                  gradientTo="#8b5cf6"
                  className="text-center relative p-8 rounded-2xl group hover:scale-105 transition-transform duration-300"
                  gradientColor="rgba(59,130,246,0.08)"
                  data-oid="ql3gc9x"
                >
                  <div className="relative mb-6" data-oid="clyg2wz">
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                      data-oid="9bzccw5"
                    >
                      <Icon className="w-8 h-8 text-white" data-oid="67ngav-" />
                    </motion.div>
                    <motion.div
                      className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg"
                      whileHover={{ scale: 1.2 }}
                      transition={{ duration: 0.2 }}
                      data-oid="-x8dq10"
                    >
                      {step.step}
                    </motion.div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3" data-oid="rditk_d">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground" data-oid="zm5w-m8">
                    {step.description}
                  </p>
                </MagicCard>
              </Container>
            );
          })}
        </div>
      </Container>

      {/* Testimonial */}
      <Container data-oid="_2enu8s">
        <motion.div
          className="text-center p-8 rounded-2xl border bg-card mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="1orsfw0"
        >
          <div className="flex justify-center mb-4" data-oid="co_a_nn">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star
                key={i}
                className="w-5 h-5 text-yellow-400 fill-current"
                data-oid="w9jwh4m"
              />
            ))}
          </div>
          <blockquote
            className="text-xl md:text-2xl font-medium mb-6 max-w-3xl mx-auto italic"
            data-oid="lh1e.6d"
          >
            "{testimonial.quote}"
          </blockquote>
          <div data-oid="lx_d9th">
            <p className="font-semibold text-lg" data-oid="ak2x1:6">
              {testimonial.author}
            </p>
            <p className="text-muted-foreground" data-oid="d-uuax3">
              {testimonial.role} at {testimonial.company}
            </p>
          </div>
        </motion.div>
      </Container>

      {/* Enhanced CTA Section */}
      <Container data-oid="hzzsy4y">
        <motion.div
          className="relative text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid=":ijq08-"
        >
          {/* Animated background elements */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{ duration: 4, repeat: Infinity }}
            data-oid="039ukj0"
          />

          <motion.div
            className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            data-oid="liqjoui"
          />

          <div className="relative z-10" data-oid="j189up8">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-oid="wpfse7:"
            >
              <Sparkles
                className="h-12 w-12 mx-auto mb-4 text-white/80"
                data-oid="ejqzj1w"
              />
            </motion.div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
              data-oid=":1bamb:"
            >
              Ready to Experience the Power of AI Marketing?
            </h2>
            <p
              className="text-xl mb-8 opacity-90 max-w-2xl mx-auto"
              data-oid="aaw79y8"
            >
              Join thousands of marketers who are already using Blyrha to
              achieve extraordinary results.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              data-oid="boqevkl"
            >
              <Link to="/demo" data-oid="vvr:o2h">
                <Button
                  size="lg"
                  variant="secondary"
                  className="px-8 bg-white text-blue-600 hover:bg-white/90"
                >
                  Start Your Free Trial
                </Button>
              </Link>
              <Link to="/contact" data-oid="n2awurp">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 border-white text-white hover:bg-white/10 backdrop-blur-sm"
                  data-oid="gcw3gjn"
                >
                  Schedule a Demo
                </Button>
              </Link>
            </div>
            <p className="text-sm opacity-70 mt-4" data-oid="rcs41i9">
              14-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </motion.div>
      </Container>
    </Wrapper>
  );
};

export default FeaturesPage;
